import { useState, useMemo } from 'react';
import { ActivityTable } from './ActivityTable.jsx';
import { CorrectiveActionBox } from './CorrectiveActionBox.jsx';
import { EmptyStatePass } from './EmptyStatePass.jsx';
import { FloatHistogram } from './FloatHistogram.jsx';
import { variantFor } from '../../pdf/helpers/check-block-assembler.js';
import { FINDING_TYPE, STATUS_THRESHOLD } from '../../checks/checkResult.js';
import {
  sortFindingsByFloatPosition,
  getFloatPositionPills,
  formatLoadingComposition,
  buildResourceLoadingAction,
} from '../../shared/check36Display.js';
import { emptyStateFor, EMPTY_PASS_MESSAGE } from '../../shared/emptyStates.js';
import { xlsxFilenameFor } from '../../shared/checkExport.js';
import {
  buildReportOnlyLine,
  NO_TABLE_BODY_TEXT,
} from '../../shared/checkDisplay.js';
import {
  BASELINE_COMPARISON_CHECKS,
  buildComparisonBody,
} from '../../shared/baselineComparisonDisplay.js';

/**
 * BASELINE-SUB4: the comparison body for a Category-7 check, rendered by `kind`.
 * Reuses the `.act-table` markup/classes so the dashboard table is coherent with
 * the PDF; the relationship line and statement render as text blocks. No
 * corrective action (REPORT_ONLY).
 *
 * MEGAC-FIX F3: #31–34 are exportable now — table/relationship bodies get an
 * Export XLSX affordance (same route as the standard-check table), matching
 * the PDF's embedded attachment for the same checks.
 */
function ComparisonExportLink({ href, filename }) {
  if (!href) return null;
  return (
    <div className="table-footer">
      <a className="btn-filled" href={href} download={filename}>
        <span aria-hidden="true">↓</span> Export XLSX
      </a>
    </div>
  );
}

function ComparisonBody({ body, xlsxHref, xlsxName }) {
  if (!body) return null;
  if (body.kind === 'emptyNote') {
    // Zero findings for a #31/#32/#33 table body — a single muted line instead
    // of a header-only table (shared wording; F6).
    return <p className="comparison-empty-note">{body.text}</p>;
  }
  if (body.kind === 'activityTable') {
    return (
      <div className="act-table-wrap">
        <table className="act-table">
          <thead>
            <tr>
              {body.columns.map((col) => (
                <th key={col.key} style={{ textAlign: col.align === 'right' ? 'right' : 'left' }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.rows.map((row, ri) => (
              <tr key={ri}>
                {body.columns.map((col) => (
                  <td
                    key={col.key}
                    className={col.key === 'taskCode' ? 'act-id' : undefined}
                    style={{ textAlign: col.align === 'right' ? 'right' : 'left' }}
                  >
                    {row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {/* G-4 (Batch-RENDER-CAPS): explicit bound note — the canonical
            truncationNote string from buildComparisonBody (Rule 12: the PDF
            renders the identical string). */}
        {body.truncationNote && (
          <p className="act-table-cap-note">{body.truncationNote}</p>
        )}
        <ComparisonExportLink href={xlsxHref} filename={xlsxName} />
      </div>
    );
  }
  if (body.kind === 'relationshipLines') {
    if (!body.lines.length) return null;
    return (
      <>
        {body.lines.map((line, i) => (
          <p key={i} className="comparison-line">{line}</p>
        ))}
        <ComparisonExportLink href={xlsxHref} filename={xlsxName} />
      </>
    );
  }
  // statement
  return (
    <div className="comparison-statement">
      <p className="comparison-statement-text">{body.text}</p>
      {body.baselineFinishLabel && body.currentFinishLabel && (
        <p className="comparison-statement-sub">
          Baseline {body.baselineFinishLabel} → Current {body.currentFinishLabel}
        </p>
      )}
    </div>
  );
}

function xlsxHrefFor(reportId, checkNumber) {
  if (!reportId) return null;
  return `/api/report/${encodeURIComponent(reportId)}/check/${checkNumber}/xlsx`;
}

function actionFromCheck(check) {
  const a = check.correctiveAction;
  if (!a) return null;
  if (typeof a === 'string') return a;
  return a.statement || a.text || a.message || a.infoText || null;
}

/**
 * Check #36 float-position pill cluster + loading composition.
 *
 * Pills are interactive filters — clicking a pill narrows the activity table
 * to that band; clicking the active pill again clears the filter. Bands with
 * count===0 still render (zero state is informative) but are non-clickable.
 */
function Check36Extras({ pills, composition, activeBand, onPillClick }) {
  return (
    <div className="check36-extras">
      {/* MEGAC-FIX F7: zero findings → getFloatPositionPills returns [] and
          the chip row is skipped entirely (rule: chip row only when
          findings > 0; matches the PDF assembler's null-out). */}
      {pills.length > 0 && (
      <div className="fp-pills" role="group" aria-label="Float-position filter">
        {pills.map((pill) => {
          const isActive = pill.band === activeBand;
          const hyphenated = pill.band.replace(/_/g, '-');
          // PDF-POLISH-1 D11: zero-count pills get a `muted` modifier so
          // the dashboard neutralizes them the same way the PDF does.
          const cls = `fp-pill ${pill.muted ? 'fp-pill-muted' : `fp-pill-${hyphenated}`}${isActive ? ' active' : ''}`;
          return (
            <button
              key={pill.band}
              type="button"
              className={cls}
              onClick={() => onPillClick(pill.band)}
              aria-pressed={isActive}
              disabled={pill.count === 0}
              data-band={pill.band}
            >
              {pill.count} {pill.label}
            </button>
          );
        })}
      </div>
      )}
      <div className="fp-composition">{composition}</div>
    </div>
  );
}

/**
 * The expanded body of a single check. Renders:
 *   1. FloatHistogram for Check #19 (uses the engine-provided buckets)
 *   2. Check #36: float-position pills + composition + sorted activity table
 *      + critical-path-aware corrective action (10D-2/Sub-2)
 *   3. EmptyStatePass when there are no findings and no table applies
 *   4. ActivityTable otherwise (imports column schema from PDF helpers)
 *   5. CorrectiveActionBox below (statement rendered verbatim)
 *
 * Reads canonical CheckResult §A1 fields: check_number, severity_findings,
 * finding_type, status_threshold, buckets / float_position_breakdown /
 * loading_type_breakdown (aux-spread).
 */
export function CheckExpanded({ check, reportId }) {
  const n = check.check_number;
  const findings = check.severity_findings || [];
  // STAGE4-FIX-B Part 1: the body routes on the PDF's own §A3 variant router
  // (variantFor, check-block-assembler) — ONE routing function, two renderers,
  // so the mirrors cannot disagree on body shape.
  const variant = variantFor(check);
  const isHistogram = variant === 'histogram' && Array.isArray(check.buckets);
  const isReportOnly = check.finding_type === FINDING_TYPE.REPORT_ONLY;
  const isComparison = BASELINE_COMPARISON_CHECKS.has(n);
  // STAGE4-FIX (v3.18 §c, D-Dash27): report-only checks with a standard column
  // schema (#5/#14/#17/#25/#27) render the REPORT ONLY chip + their findings
  // table — never the pass-styled empty state (that wording is reserved for
  // activity-level threshold passes, §A3).
  const isReportOnlyBody = variant === 'reportOnly';
  // FIX-B: summary-only checks (#9/#20/#21/#22, incl. report-only #21) render
  // the PDF's exact neutral treatment — the shared line in a neutral box.
  const isNoTableBody = variant === 'noTable';
  // True finding total: the dashboard.json projection caps the rows array —
  // the real count survives via severity_findings_total (G-4).
  const reportOnlyTotal = check.severity_findings_total ?? findings.length;

  const [activeBand, setActiveBand] = useState(null);

  const sortedCheck36Findings = useMemo(
    () => (n === 36 ? sortFindingsByFloatPosition(findings) : findings),
    [n, findings],
  );

  const filteredCheck36Findings = useMemo(() => {
    if (n !== 36) return findings;
    if (!activeBand) return sortedCheck36Findings;
    return sortedCheck36Findings.filter((f) => f.float_position === activeBand);
  }, [n, findings, sortedCheck36Findings, activeBand]);

  const onPillClick = (band) => {
    setActiveBand((prev) => (prev === band ? null : band));
  };

  let action = actionFromCheck(check);
  if (n === 36) {
    // 10D-3/Sub-2: thread engine-attached mode into the K/M/L override so
    // the dashboard mirrors the per-check statement's tone (Rule G/C/R).
    const mode = check.correctiveAction?.mode || 'general';
    const adaptive = buildResourceLoadingAction(check, mode);
    if (adaptive) action = adaptive;
  }
  // CERT-FIX-1 Part 5 (v3.17): Tier-2 verification instruction, verbatim from
  // the emission (null for Tier 1) — closes the box on both surfaces (Rule 3).
  const verificationNote = check.correctiveAction?.verificationNote || null;
  // FIX-B (parity): the corrective-action box gates EXACTLY as the PDF does
  // (§A2 — action renders for EXCEEDED only; PASS = no problem, no action;
  // report-only emissions carry isReportOnly and never render the box).
  // Previously the dashboard showed the box whenever the emission carried any
  // text — including PASS and report-only checks the PDF leaves bare.
  const showAction =
    check.status_threshold === STATUS_THRESHOLD.EXCEEDED
    && !!action
    && !check.correctiveAction?.isReportOnly;

  if (n === 36 && !isReportOnly) {
    const pills = getFloatPositionPills(check);
    const composition = formatLoadingComposition(check);
    const rows = filteredCheck36Findings;
    return (
      <div className="check-expanded">
        <Check36Extras
          pills={pills}
          composition={composition}
          activeBand={activeBand}
          onPillClick={onPillClick}
        />
        {rows.length > 0 ? (
          <ActivityTable
            checkNumber={n}
            checkName={check.check_name}
            findings={rows}
            xlsxDownloadHref={xlsxHrefFor(reportId, n)}
            totalCount={check.severity_findings_total ?? findings.length}
          />
        ) : findings.length === 0 && check.status_threshold === STATUS_THRESHOLD.EXCEEDED ? (
          /* G-2: zero-denominator #36 — the not-assessable banner, same
             canonical text as the PDF emptyExceeded body (Rule 12). */
          <div className="empty-state-exceeded" data-testid="empty-state-exceeded">
            <div>{emptyStateFor(check).message}</div>
            {emptyStateFor(check).note && (
              <div className="empty-state-exceeded-note">{emptyStateFor(check).note}</div>
            )}
          </div>
        ) : findings.length === 0 ? (
          /* F7 (B-Check36EmptyState, v3.36): #36 passed — all in-scope
             activities are loaded, so there are no findings and no active
             filter. Show the standard PASS empty state (mirrors the PDF
             emptyPass path), never the filter-empty message. The pill row
             already hides (getFloatPositionPills returns [] at zero findings). */
          <EmptyStatePass message={EMPTY_PASS_MESSAGE} />
        ) : (
          <EmptyStatePass message="No activities match the selected filter." />
        )}
        {showAction && (
          <CorrectiveActionBox action={action} verificationNote={verificationNote} />
        )}
      </div>
    );
  }

  // BASELINE-SUB4: the comparison checks (#31–35) render a structured body
  // (activity table / relationship line / statement) instead of the generic
  // report-only EmptyStatePass. No CorrectiveActionBox (REPORT_ONLY).
  // MEGAC-FIX F3: #31–34 with findings get the Export XLSX affordance (#35
  // stays inline-only, matching the PDF attachment set).
  if (isComparison) {
    const comparisonExportable = n !== 35 && findings.length > 0;
    return (
      <div className="check-expanded">
        <ComparisonBody
          body={buildComparisonBody(check)}
          xlsxHref={comparisonExportable ? xlsxHrefFor(reportId, n) : null}
          xlsxName={comparisonExportable ? xlsxFilenameFor(n, check.check_name) : null}
        />
      </div>
    );
  }

  return (
    <div className="check-expanded">
      {isHistogram && (
        <FloatHistogram
          buckets={check.buckets}
          distributionSummary={check.distribution_summary}
        />
      )}

      {variant === 'histogram' && !isHistogram && (
        /* Degenerate #19 payload without buckets — neutral, never green. */
        <div className="no-table-body" data-testid="no-table-body">
          <div className="no-table-line">{NO_TABLE_BODY_TEXT}</div>
        </div>
      )}

      {(variant === 'table' || variant === 'structural') && findings.length > 0 && (
        <ActivityTable
          checkNumber={n}
          checkName={check.check_name}
          findings={findings}
          xlsxDownloadHref={xlsxHrefFor(reportId, n)}
          totalCount={check.severity_findings_total ?? findings.length /* G-4: projection caps the array — the true total survives via severity_findings_total */}
        />
      )}

      {(variant === 'emptyPass' || variant === 'emptyExceeded'
        || (variant === 'structural' && findings.length === 0)) && (
        check.status_threshold === STATUS_THRESHOLD.EXCEEDED ? (
          /* G-2 (Batch-RENDER-36): EXCEEDED with zero findings = the
             not-assessable population — never the PASS-styled empty body.
             Same canonical text as the PDF emptyExceeded variant (Rule 12). */
          <div className="empty-state-exceeded" data-testid="empty-state-exceeded">
            <div>{emptyStateFor(check).message}</div>
            {emptyStateFor(check).note && (
              <div className="empty-state-exceeded-note">{emptyStateFor(check).note}</div>
            )}
          </div>
        ) : check.status_threshold === STATUS_THRESHOLD.PASS ? (
          /* FIX-B: the green pass body renders ONLY on status_threshold PASS. */
          <EmptyStatePass message="This check met its threshold — no activities flagged." />
        ) : (
          /* Degenerate non-PASS, non-EXCEEDED empty state — neutral, never green. */
          <div className="no-table-body" data-testid="no-table-body">
            <div className="no-table-line">{NO_TABLE_BODY_TEXT}</div>
          </div>
        )
      )}

      {isReportOnlyBody && (
        <div className="report-only-body" data-testid="report-only-body">
          {/* X-4 (V-21): the REPORT ONLY chip now renders in the check HEADER
              (CheckBlock, top-right) like #21 and the comparison set — no longer
              duplicated here. The shared count line stays (string from the same
              source the PDF consumes, checkDisplay.js). */}
          <div className="report-only-chip-row">
            <span className="report-only-line">{buildReportOnlyLine(reportOnlyTotal)}</span>
          </div>
          {findings.length > 0 && (
            <ActivityTable
              checkNumber={n}
              checkName={check.check_name}
              findings={findings}
              xlsxDownloadHref={xlsxHrefFor(reportId, n)}
              totalCount={reportOnlyTotal}
            />
          )}
        </div>
      )}

      {isNoTableBody && (
        /* FIX-B Part 1: summary-only body — the PDF's exact neutral treatment
           (shared NO_TABLE_BODY_TEXT in a neutral box, never pass-styled).
           The export affordance mirrors the PDF's attachment rule: an XLSX
           exists exactly when the check carries findings (emitExport). */
        <div className="no-table-body" data-testid="no-table-body">
          <div className="no-table-line">{NO_TABLE_BODY_TEXT}</div>
          {findings.length > 0 && (
            <div className="table-footer">
              <a
                className="btn-filled"
                href={xlsxHrefFor(reportId, n)}
                download={xlsxFilenameFor(n, check.check_name)}
              >
                <span aria-hidden="true">↓</span> Export XLSX
              </a>
            </div>
          )}
        </div>
      )}

      {showAction && (
        <CorrectiveActionBox action={action} verificationNote={verificationNote} />
      )}
    </div>
  );
}
